"use client"

import type { TransactionIdentifier as GraphQLTransactionIdentifier } from "@opensea/graphql"
import {
  Spinner,
  Toast,
  ToastAvatar,
  ToastContent,
  ToastErrorIcon,
  ToastSide,
  ToastSuccessIcon,
  ToastTitle,
  useToasts,
} from "@opensea/ui-kit"
import type { Nullable } from "@opensea/utils"
import { useEffect, useMemo, useRef } from "react"
import { useTranslations } from "use-intl"
import { useTransactionStatus } from "../../../polychain/hooks/useTransactionStatus"
import { useRenderImage } from "../../../providers/RenderImageProvider"
import { FlowActions } from "../../../stores/flowStore"

type TokenClaimToastProps = {
  tokenReward: {
    id: string
    balance?: {
      quantity: string
      usdValue: string
      currency: {
        name?: string
        symbol?: string
        imageUrl?: Nullable<string>
        chainIdentifier: string
        contractAddress: string
        restrictSwapsToNativeTokens?: Nullable<boolean>
        restrictSwapsToSameChain?: Nullable<boolean>
      }
    }
  }
  transactionIdentifier: GraphQLTransactionIdentifier
}

export function TokenClaimToast({
  tokenReward,
  transactionIdentifier,
}: TokenClaimToastProps) {
  const t = useTranslations("wallet.TokenClaimToast")
  const { renderImage } = useRenderImage()
  const { removeToast } = useToasts()
  const hasCalledOnSuccess = useRef(false)

  const transactionStatus = useTransactionStatus({
    transaction: {
      chain: transactionIdentifier.chain,
      hash: transactionIdentifier.transactionHash,
    },
  })

  // Call onSuccess when transaction succeeds
  useEffect(() => {
    if (transactionStatus === "success" && !hasCalledOnSuccess.current) {
      hasCalledOnSuccess.current = true
      FlowActions.onSuccess()
    }
  }, [transactionStatus])

  useEffect(() => {
    if (transactionStatus === "success" || transactionStatus === "error") {
      const timerId = setTimeout(
        () => {
          removeToast("token-claim-toast")
        },
        transactionStatus === "error" ? 4000 : 1500,
      )

      return () => clearTimeout(timerId)
    }
  }, [transactionStatus, removeToast])

  const tokenImageUrl = useMemo(() => {
    return tokenReward.balance?.currency?.imageUrl || null
  }, [tokenReward.balance?.currency?.imageUrl])

  const { icon, title } = useMemo(() => {
    switch (transactionStatus) {
      case "pending":
        return {
          icon: <Spinner size="sm" />,
          title: t("claimPending"),
        }
      case "error":
        return {
          icon: <ToastErrorIcon />,
          title: t("claimFailed"),
        }
      case "success":
        return {
          icon: <ToastSuccessIcon />,
          title: t("claimSuccessful"),
        }
      default:
        return {
          icon: <Spinner size="sm" />,
          title: t("claimPending"),
        }
    }
  }, [transactionStatus, t])

  return (
    <Toast>
      <ToastAvatar
        renderImage={renderImage}
        seed={`${tokenReward.balance?.currency?.chainIdentifier}:${tokenReward.balance?.currency?.contractAddress}`}
        src={tokenImageUrl || ""}
      />
      <ToastContent>
        <ToastTitle>{title}</ToastTitle>
      </ToastContent>
      <ToastSide>{icon}</ToastSide>
    </Toast>
  )
}
